import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, MenuItem, Select, FormControl,
  InputLabel, Chip, SelectChangeEvent, Alert, FormControlLabel, Switch,
  Divider, Checkbox, Snackbar, Tooltip,
} from '@mui/material';
import { Add, Edit, Upload, Delete, PhonelinkLock } from '@mui/icons-material';
import api from '../../api/client';

interface Person {
  id: number; dsn: string; first_name: string; last_name: string;
  agency_id: number; agency_name: string; email: string;
  sms_email: string; mobile_access: boolean; mobile_username: string;
  is_active: boolean; totp_configured: boolean;
}
interface Agency { id: number; name: string; }

export default function PersonnelPage() {
  const [people, setPeople] = useState<Person[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Person | null>(null);
  const [form, setForm] = useState({ dsn: '', first_name: '', last_name: '', agency_id: 0, email: '', sms_email: '', mobile_access: false, mobile_username: '', mobile_password: '' });
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [snack, setSnack] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => {
    api.get('/personnel').then((r) => { setPeople(r.data); setSelected(new Set()); });
    api.get('/agencies').then((r) => setAgencies(r.data));
  };
  useEffect(() => { load(); }, []);

  const handleOpen = (p?: Person) => {
    setError('');
    if (p) {
      setEditing(p);
      setForm({ dsn: p.dsn, first_name: p.first_name, last_name: p.last_name, agency_id: p.agency_id, email: p.email || '', sms_email: p.sms_email || '', mobile_access: p.mobile_access, mobile_username: p.mobile_username || '', mobile_password: '' });
    } else {
      setEditing(null);
      setForm({ dsn: '', first_name: '', last_name: '', agency_id: agencies[0]?.id || 0, email: '', sms_email: '', mobile_access: false, mobile_username: '', mobile_password: '' });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      setError('');
      const payload = { ...form };
      if (editing && !payload.mobile_password) {
        const { mobile_password, ...rest } = payload;
        await api.patch(`/personnel/${editing.id}`, rest);
      } else if (editing) {
        await api.patch(`/personnel/${editing.id}`, payload);
      } else {
        await api.post('/personnel', payload);
      }
      setOpen(false);
      load();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Failed to save personnel. DSN may already exist.';
      setError(msg);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/personnel/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSnack(`Imported: ${r.data.created} created, ${r.data.skipped} skipped`);
      load();
    } catch { setSnack('Import failed'); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleBulkDelete = async () => {
    if (!selected.size || !window.confirm(`Delete ${selected.size} personnel record(s)?`)) return;
    await api.post('/personnel/bulk-delete', { ids: Array.from(selected) });
    setSnack(`Deleted ${selected.size} personnel record(s)`);
    load();
  };

  const toggleSelect = (id: number) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === people.length) setSelected(new Set());
    else setSelected(new Set(people.map((p) => p.id)));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Personnel</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selected.size > 0 && (
            <Button startIcon={<Delete />} color="error" variant="outlined" onClick={handleBulkDelete}>
              Delete ({selected.size})
            </Button>
          )}
          <input type="file" accept=".csv" ref={fileRef} hidden onChange={handleImport} />
          <Button startIcon={<Upload />} variant="outlined" onClick={() => fileRef.current?.click()}>Import CSV</Button>
          <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen()}>Add Personnel</Button>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"><Checkbox checked={people.length > 0 && selected.size === people.length} indeterminate={selected.size > 0 && selected.size < people.length} onChange={toggleAll} /></TableCell>
              <TableCell>DSN</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Agency</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>2FA</TableCell>
              <TableCell>Status</TableCell>
              <TableCell width={80} />
            </TableRow>
          </TableHead>
          <TableBody>
            {people.map((p) => (
              <TableRow key={p.id} selected={selected.has(p.id)}>
                <TableCell padding="checkbox"><Checkbox checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} /></TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{p.dsn}</TableCell>
                <TableCell>{p.last_name}, {p.first_name}</TableCell>
                <TableCell>{p.agency_name}</TableCell>
                <TableCell>{p.mobile_access ? <Chip size="small" label="Yes" color="info" /> : '—'}</TableCell>
                <TableCell>
                  {p.totp_configured
                    ? <Chip size="small" label="Active" color="success" />
                    : <Chip size="small" label="Off" variant="outlined" />}
                </TableCell>
                <TableCell><Chip size="small" label={p.is_active ? 'Active' : 'Inactive'} color={p.is_active ? 'success' : 'default'} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {p.totp_configured && (
                    <Tooltip title="Reset TOTP">
                      <IconButton size="small" color="warning" onClick={async () => {
                        if (!window.confirm(`Reset TOTP for ${p.first_name} ${p.last_name}? They will need to set up their authenticator app again on next MDT login.`)) return;
                        try { await api.post(`/personnel/${p.id}/reset-totp`); load(); } catch { alert('Failed to reset TOTP'); }
                      }}><PhonelinkLock fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                  <IconButton size="small" onClick={() => handleOpen(p)}><Edit fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Personnel' : 'New Personnel'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
          <TextField fullWidth label="DSN" margin="normal" value={form.dsn} onChange={(e) => setForm({ ...form, dsn: e.target.value })} />
          <TextField fullWidth label="First Name" margin="normal" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          <TextField fullWidth label="Last Name" margin="normal" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          <TextField fullWidth label="Email" margin="normal" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="officer@agency.gov" />
          <TextField fullWidth label="SMS Email (carrier gateway)" margin="normal" value={form.sms_email} onChange={(e) => setForm({ ...form, sms_email: e.target.value })} placeholder="5551234567@vtext.com" />
          <FormControl fullWidth margin="normal">
            <InputLabel>Agency</InputLabel>
            <Select value={String(form.agency_id)} label="Agency" onChange={(e: SelectChangeEvent) => setForm({ ...form, agency_id: Number(e.target.value) })}>
              {agencies.map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>MDT / Mobile Access</Typography>
          <FormControlLabel
            control={<Switch checked={form.mobile_access} onChange={(e) => setForm({ ...form, mobile_access: e.target.checked })} />}
            label="Enable Mobile Access"
          />
          {form.mobile_access && (
            <>
              <TextField fullWidth label="Mobile Username" margin="normal" value={form.mobile_username} onChange={(e) => setForm({ ...form, mobile_username: e.target.value })} />
              <TextField fullWidth label={editing ? 'New Password (leave blank to keep)' : 'Password'} type="password" margin="normal" value={form.mobile_password} onChange={(e) => setForm({ ...form, mobile_password: e.target.value })} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')}>
        <Alert severity="info" onClose={() => setSnack('')}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
}
